import * as Yup from 'yup'
import { getIn, useFormik } from 'formik'
import React, { useEffect, useState, useCallback } from 'react'
import { CharCount, InputWrapper, LabelWrapper } from '@/components/Input.styles'
import TextField from '@/components/TextField'
import InputError from '@/components/InputError'
import TextArea from '@/components/TextArea'
import { BookingStepButtons, FieldsWrapper } from '@/components/VoucherSelector.styles'
import { defaultVoucherFields } from '@/constant/voucher'
import { Button } from '@big-red-group/storefront-common-ui'
import debounce from 'lodash.debounce'
import { GiftVoucherField, VoucherField } from '@big-red-group/storefront-common-checkout'

const VoucherFieldsForm: React.FC<{
  setVoucherFields: (fields: VoucherField[]) => void
  compileIndividualFields: (optionId: string, optionIndex: number, fields: VoucherField[]) => void
  setHasCompletedFieldsForm: () => void
  fields?: GiftVoucherField[]
  hasMultipleMessages?: boolean
  optionId?: string
  optionIndex?: number
}> = ({
  setVoucherFields,
  compileIndividualFields,
  setHasCompletedFieldsForm,
  fields = defaultVoucherFields,
  hasMultipleMessages = false,
  optionId,
  optionIndex,
}) => {
  const [isFormComplete, setIsFormComplete] = useState<boolean>(false)

  const sortedFields = fields?.sort((a, b) => a.order - b.order)

  const validationSchema = Yup.object().shape({
    fields: Yup.array().of(
      Yup.object().shape({
        ...sortedFields?.reduce((fields: {}, field: GiftVoucherField) => {
          if (field.required) {
            // @ts-ignore
            fields[field.key] = Yup.string()
              .required(`Please enter '${field.title}'`)
              .min(field.minLength, `'${field.title}' field must be ${field.minLength} characters`)
          } else {
            // @ts-ignore
            fields[field.key] = Yup.string().min(
              field.minLength,
              `'${field.title}' field must be ${field.minLength} characters`
            )
          }
          return fields
        }, {}),
      })
    ),
  })

  function getFieldInitialValue(field: GiftVoucherField): VoucherField {
    return { [field.key]: field.value || '' }
  }

  const getFieldsInitialValues = (): VoucherField[] => {
    return sortedFields.reduce((values: VoucherField[], field: GiftVoucherField) => {
      return [...values, getFieldInitialValue(field)]
    }, [])
  }

  const formik = useFormik({
    initialValues: {
      fields: getFieldsInitialValues(),
    },
    validationSchema,
    onSubmit: async () => {},
  })

  const handleFormChange = useCallback(
    debounce(async () => {
      await formik.validateForm()
      if (!hasMultipleMessages) {
        setVoucherFields(formik.values.fields)
      }
      let hasErrors = false
      fields.forEach((field: GiftVoucherField, index) => {
        if (!formik.errors.fields?.length) {
          return
        }
        if (Object.keys(formik.errors.fields[index]).includes(field.key)) {
          hasErrors = true
        }
      })
      setIsFormComplete(!hasErrors)
    }, 100) as Function,
    [formik.values, formik.errors]
  )

  useEffect(() => {
    handleFormChange()
  }, [formik.values, formik.errors, handleFormChange])

  return (
    <FieldsWrapper>
      {formik.values.fields && fields?.length
        ? formik.values.fields.map((field: VoucherField, index: number) => {
            const fieldValue = fields[index]
            const touched = formik.touched.fields?.length && getIn(formik.touched.fields[index], fieldValue.key)
            const errors = formik.errors.fields?.length && getIn(formik.errors.fields[index], fieldValue.key)
            const inputValue = getIn(formik.values, `fields[${index}].[${fields[index].key}]`)
            return (
              <div key={fieldValue.name}>
                <div data-testid="extra-fields" key={index} className="full-width-input">
                  <InputWrapper>
                    <LabelWrapper>
                      <label className={fieldValue.required ? 'required' : ''} htmlFor={fieldValue.key}>
                        {fieldValue.title}
                      </label>
                      {fieldValue.maxLength && fieldValue.type === 'LONGTEXT' && (
                        <CharCount reachedMax={inputValue.length >= fieldValue.maxLength}>
                          {inputValue.length}/{fieldValue.maxLength}
                        </CharCount>
                      )}
                    </LabelWrapper>
                    {fieldValue.type === 'TEXT' && (
                      <TextField
                        name={`fields[${index}].${fields?.[index].key}`}
                        placeholder={`${fields?.[index].placeholder}`}
                        value={getIn(formik.values, `fields[${index}].[${fields[index].key}]`)}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={touched && !!errors}
                        fieldId={fieldValue.key}
                        maxLength={fieldValue.maxLength}
                      />
                    )}
                    {fieldValue.type === 'LONGTEXT' && (
                      <TextArea
                        name={`fields[${index}].${fields?.[index].key}`}
                        placeholder={`${fields?.[index].placeholder}`}
                        value={getIn(formik.values, `fields[${index}].[${fields[index].key}]`)}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={touched && !!errors}
                        maxlength={fieldValue.maxLength}
                        minLength={fieldValue.minLength}
                      />
                    )}
                    {touched && !!errors && <InputError dataTestId={`${fieldValue.name}-error`}>{errors}</InputError>}
                  </InputWrapper>
                </div>
              </div>
            )
          })
        : null}
      <BookingStepButtons>
        <Button
          size="S"
          onClick={() =>
            hasMultipleMessages
              ? compileIndividualFields(optionId!, optionIndex!, formik.values.fields)
              : setHasCompletedFieldsForm()
          }
          dataTestId="voucher-confirm-fields"
          disabled={!isFormComplete}
        >
          Confirm
        </Button>
      </BookingStepButtons>
    </FieldsWrapper>
  )
}

export default VoucherFieldsForm
